import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../../util_components/common_header';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import CustomDatePicker from '../../../util_components/custom_date_picker';
import Paginator from '../../../util_components/paginator';

import { get_date_filter_data_timezone_utc } from '../../../../util_functions';
import { day_format_year, time_format, date_drop_for_report } from '../../../../constants';
import { post_api } from '../../../../redux/api_funcs';
import * as actions from '../../../../redux/action_creators';
import { get_api_url } from '../../../../utils/urls';
import {
  parse_filters,
  build_search,
  MOBILE_VERIFIED_OPTIONS,
  DEFAULT_DATE_FILTER
} from './filters';

import 'react-datepicker/dist/react-datepicker.css';

const TICK_ICON = 'https://images.myyogateacher.com/icons/ic-checkmark-48.png';
const CROSS_ICON = 'https://images.myyogateacher.com/icons/ic-delete-48.png';

// Students who gave an email or phone during signup but never finished onboarding (PROD-9198).
class IncompleteOnboarding extends Component {
  constructor(props) {
    super(props);
    // The URL is the source of truth on entry, so a shared link lands on the same filters.
    this.state = {
      loading: true,
      students: [],
      total_count: 0,
      page_size: 50,
      ...parse_filters(props.location.search, moment().format('YYYY-MM-DD'))
    };
  }

  componentDidMount() {
    this.load_data();
  }

  // Mirrors filters into the URL; replace, not push, so paging doesn't fill the back button.
  sync_url = () => {
    const { date_filter_type, start_date, end_date, mobile_verified, page } = this.state;
    const search = build_search({ date_filter_type, start_date, end_date, mobile_verified, page });
    if (search !== this.props.location.search) {
      this.props.history.replace({ pathname: this.props.location.pathname, search });
    }
  };

  load_data = (page = this.state.page) =>
    this.setState({ loading: true, page }, () => {
      this.sync_url();
      const { date_filter_type, start_date, end_date, mobile_verified } = this.state;
      const url = get_api_url('get_incomplete_onboarding_students');
      const date_data = get_date_filter_data_timezone_utc(
        date_filter_type,
        start_date,
        end_date,
        this.props.iana_timezone
      );

      const payload = {
        start_date: date_data.utc_start_date,
        end_date: date_data.utc_end_date,
        page
      };
      // Omitted entirely when the filter is off — the endpoint reads absent as "both".
      if (mobile_verified !== '') payload.mobile_verified = Number(mobile_verified);

      post_api(url, payload, true)
        .then((res) => {
          this.setState({
            loading: false,
            students: [...res.data.student_data],
            total_count: res.data.total_count,
            page: res.data.page,
            page_size: res.data.page_size
          });
        })
        .catch((e) => {
          let err_message;
          try {
            err_message = e.response.data.message;
          } catch (err) {
            err_message = 'Some error occured. Please contact dev team.';
          }
          if (typeof err_message !== 'string') {
            err_message = 'Some error occured. Please contact dev team.';
          }
          this.props.set_notification_variable(true, 'error', err_message);
          this.setState({ loading: false });
        });
    });

  // New population, so reset the page; the select's own blank option means today, not unfiltered.
  change_date_filter = (e) =>
    this.setState({ date_filter_type: e.target.value || DEFAULT_DATE_FILTER, page: 1 }, () => {
      if (this.state.date_filter_type !== 'CUSTOM') {
        this.load_data(1);
      }
    });

  handle_date_change = (name, val) => this.setState({ [name]: val, page: 1 });

  change_mobile_verified = (e) =>
    this.setState({ mobile_verified: e.target.value, page: 1 }, () => this.load_data(1));

  submit_custom_range = () => this.load_data(1);

  change_page = (page) => this.load_data(page);

  render_filters = () => {
    const { date_filter_type, start_date, end_date, mobile_verified } = this.state;

    return (
      <div
        style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' }}
      >
        <div style={{ marginRight: '10px' }}>
          <CustomSingleSelect
            label="Select a date filter"
            options={date_drop_for_report}
            onChange={this.change_date_filter}
            value={date_filter_type}
            style={{ width: '200px' }}
          />
        </div>
        {date_filter_type === 'CUSTOM' ? (
          <>
            <div className="daily-report-item">
              <CustomDatePicker
                label="Start Date"
                value={start_date}
                handleChange={(val) => this.handle_date_change('start_date', val)}
                maxDate={end_date}
              />
            </div>
            <div className="daily-report-item">
              <CustomDatePicker
                label="End Date"
                value={end_date}
                handleChange={(val) => this.handle_date_change('end_date', val)}
                minDate={start_date}
                maxDate={moment().format('YYYY-MM-DD')}
              />
            </div>
            <button onClick={this.submit_custom_range}>Submit</button>
          </>
        ) : null}
        <div style={{ marginRight: '10px' }}>
          <CustomSingleSelect
            label="Mobile verified"
            options={MOBILE_VERIFIED_OPTIONS}
            onChange={this.change_mobile_verified}
            value={mobile_verified}
            defaultOption="ALL"
            style={{ width: '160px' }}
          />
        </div>
      </div>
    );
  };

  render_value = (value) => (value === null || value === '' ? '--' : value);

  render_single_student = (student) => {
    const full_name = `${student.first_name} ${student.last_name}`.trim();
    const display_name = full_name === '' ? '--' : full_name;
    // A lead has no account yet, so there is no profile to link to.
    const is_lead = student.account_type === 'LEAD';

    return (
      <div className="ct-row" key={student.row_key}>
        <div className="ct-col ct-hf ct-sm-font">
          <div>
            {moment(student.created_date).tz(this.props.iana_timezone).format(day_format_year)}
          </div>
          <div>{moment(student.created_date).tz(this.props.iana_timezone).format(time_format)}</div>
        </div>
        <div className="ct-col ct-la">
          {is_lead ? (
            display_name
          ) : (
            <a className="link-no-dec" href={`/studentprofile?id=${student.uuid}`}>
              {display_name}
            </a>
          )}
        </div>
        <div className="ct-col ct-qf ct-sm-font">{is_lead ? 'Lead' : 'Student'}</div>
        <div className="ct-col ct-la ct-sm-font">{this.render_value(student.email)}</div>
        <div className="ct-col ct-hf ct-sm-font">{this.render_value(student.mobile_number)}</div>
        <div className="ct-col ct-si ct-qf">
          {student.is_mobile_verified ? (
            <img src={TICK_ICON} alt="verified" />
          ) : (
            <img src={CROSS_ICON} alt="not verified" />
          )}
        </div>
        <div className="ct-col ct-qf">{this.render_value(student.age)}</div>
        <div className="ct-col ct-hf ct-sm-font">{this.render_value(student.city)}</div>
        <div className="ct-col ct-hf ct-sm-font">{this.render_value(student.country)}</div>
        <div className="ct-col ct-sm-font">{this.render_value(student.iana_timezone)}</div>
      </div>
    );
  };

  render_data = () => {
    const { students, total_count, page, page_size } = this.state;
    const page_limit = Math.max(1, Math.ceil(total_count / page_size));

    return (
      <>
        <div className="cus-table">
          <div className="ct-row ct-h">
            <div className="ct-col ct-hf ct-sm-font">Signed Up</div>
            <div className="ct-col ct-la">Name</div>
            <div className="ct-col ct-qf ct-sm-font">Account</div>
            <div className="ct-col ct-la ct-sm-font">Email</div>
            <div className="ct-col ct-hf ct-sm-font">Mobile</div>
            <div className="ct-col ct-qf ct-sm-font">Mobile Verified</div>
            <div className="ct-col ct-qf">Age</div>
            <div className="ct-col ct-hf ct-sm-font">City</div>
            <div className="ct-col ct-hf ct-sm-font">Country</div>
            <div className="ct-col ct-sm-font">Timezone</div>
          </div>
          {students.map(this.render_single_student)}
        </div>
        {total_count === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
            No students found for the selected filters.
          </div>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
              {students.length === 0
                ? `Page ${page} is past the end of ${total_count} results.`
                : `Showing ${(page - 1) * page_size + 1}\u2013${Math.min(
                    page * page_size,
                    total_count
                  )} of ${total_count}`}
            </div>
            <Paginator curr_page={page} page_limit={page_limit} pageChange={this.change_page} />
          </div>
        )}
      </>
    );
  };

  render() {
    const { loading } = this.state;
    return (
      <CommonHeader loading={loading} title="Incomplete Onboarding">
        <div style={{ minHeight: '400px' }}>
          {this.render_filters()}
          {this.render_data()}
        </div>
      </CommonHeader>
    );
  }
}

const mapStateToProps = (state) => ({
  iana_timezone: state.home.iana_timezone
});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(IncompleteOnboarding));
